import Link from '@fuse/core/Link';
import useQuery from '@fuse/hooks/useQuery';
import { Button, CircularProgress } from '@mui/material';
import { useCallback, useEffect, useRef, useLayoutEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { MfaFields, MfaType, UsernamePasswordFields, useJwtSignInForm } from './SignInComponents';
import SignInSocialButtons from './SignInSocialButtons';
import SocialOtpForm from './SocialOtpForm';
import toast from 'react-hot-toast';
import FuseLoading from '@/@fuse/core/FuseLoading';

/**
 * JWT Sign-In Form Component
 * 
 * A comprehensive authentication form that handles:
 * - Standard username/password authentication
 * - Multi-factor authentication (TOTP, email, recovery codes)
 * - Social authentication (Google)
 * - Remember me functionality for 2FA
 * - Password visibility toggle
 * - Form validation and error handling
 * 
 * @component
 * @returns {React.ReactElement} The complete sign-in form interface
 * 
 * @example
 * ```tsx
 * <JwtSignInForm />
 * ```
 */
function JwtSignInForm(): React.ReactElement {
  const {
    // Form state
    control,
    formState,
    handleSubmit,
    trigger,

    // UI state
    passwordVisibility,
    loading,
    otpRequired,
    showSocialOtp,
    socialIdentifier,
    accessToken,

    // MFA state
    mfaType,
    initialType,
    alternativeMethods,

    // Handlers
    onSubmit,
    togglePasswordVisibility,
    toggleMfaMode,
    handleSocialOtpSuccess,
    isSubmitDisabled,
    requestAlternativeOtp,
    panelLogin,
    signOut
  } = useJwtSignInForm();

  const params = useQuery();
  const flag = params.get('flag') ?? params.get('tag');
  const [, setSearchParams] = useSearchParams();

  // Track if we've already processed this flag to prevent spam
  const processedFlagRef = useRef<string | null>(null);

  const [panelLoading, setPanelLoading] = useState<boolean>(false);

  const handlePanelLogin = useCallback(async () => {
    try {
      setPanelLoading(true);
      await panelLogin(flag);
      toast.success('Successfully signed in via universal login link');
    } catch (_error) {
      console.warn('Error with universal login link:', _error);
      toast.error('Error with universal login link');
    } finally {
      setPanelLoading(false);
    }
  }, [flag, panelLogin]);

  useEffect(() => {
    if (flag && flag !== processedFlagRef.current) {
      processedFlagRef.current = flag;
      const existingToken = localStorage.getItem('jwt_access_token');

      if (existingToken && signOut) {
        // Soft sign-out: clear the existing session without the hard
        // redirect/page-reload, since we're about to log the new user
        // in via the flag immediately below in this same effect.
        signOut({ redirect: false });
      }

      // Remove the flag parameter from the URL through React Router's own API -
      // a raw history.replaceState() call here only updates the visible browser
      // URL, not React Router's internal location state. That desync meant
      // useLocation() (and anything reading location via withRouter, like
      // FuseAuthorization's guard) kept seeing the flag as still present on the
      // next unrelated re-render, which re-triggered this whole effect against
      // a second, already-mounted-fresh JwtSignInForm and reprocessed the same
      // (likely single-use) flag a second time.
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('flag');
        next.delete('tag');
        return next;
      }, { replace: true });

      handlePanelLogin();
    }
  }, [flag, handlePanelLogin, signOut, setSearchParams]);

  useLayoutEffect(() => {
    if (globalThis.window?.sessionStorage) {
      const redirectStorageKey = 'fuseRedirectUrl';
      const redirectUrl = globalThis.window.sessionStorage.getItem(redirectStorageKey);

      globalThis.window.sessionStorage.clear();

      // Preserve auth redirect target so deep links continue after sign-in.
      if (redirectUrl?.startsWith('/')) {
        globalThis.window.sessionStorage.setItem(redirectStorageKey, redirectUrl);
      }
    }
  }, []);

  if (panelLoading) {
    return <FuseLoading />;
  }

  // Show social OTP form if socialIdentifier is set and showSocialOtp is true
  if (showSocialOtp && socialIdentifier && mfaType) {
    return (
      <div className="mt-12 flex w-full flex-col justify-center">
        <h2 className="text-xl font-semibold mb-16">Social Login Verification</h2>
        <p className="mb-24">Please verify your identity using the requested authentication method.</p>
        <SocialOtpForm
          socialIdentifier={socialIdentifier}
          mfaType={mfaType}
          initialType={initialType as MfaType}
          onSuccess={handleSocialOtpSuccess}
        />
      </div>
    );
  }

  return (
    <form
      name="loginForm"
      noValidate
      className="mt-12 flex w-full flex-col justify-center space-y-16"
      onSubmit={handleSubmit(onSubmit)}
    >
      {!otpRequired && (
        <UsernamePasswordFields
          control={control}
          errors={formState.errors}
          loading={loading}
          passwordVisibility={passwordVisibility}
          onTogglePasswordVisibility={togglePasswordVisibility}
          trigger={trigger}
        />
      )}

      {otpRequired && !socialIdentifier && mfaType && (
        <MfaFields
          control={control}
          errors={formState.errors}
          loading={loading}
          mfaType={mfaType}
          onToggleMfaMode={toggleMfaMode}
          alternativeMethods={alternativeMethods}
          onRequestAlternativeOtp={requestAlternativeOtp}
        />
      )}

      {!otpRequired && (
        <div className="flex flex-col items-center justify-center sm:flex-row sm:justify-end">
          <Link className="text-md font-medium" to="/forgot-password">
            Forgot password?
          </Link>
        </div>
      )}

      <Button
        variant="contained"
        color="primary"
        className="w-full mt-24"
        aria-label="Sign in"
        disabled={isSubmitDisabled()}
        type="submit"
        size="large"
      >
        {loading && <CircularProgress size={15} className="mr-5" />}
        {otpRequired ? 'Verify OTP' : 'Sign in'}
      </Button>

      <SignInSocialButtons disabled={Boolean(accessToken) || loading} />
    </form>
  );
}

export default JwtSignInForm;